import { BACKSPACE, DELETE, TAB, hasModifierKey, ENTER } from '@angular/cdk/keycodes';
import * as i0 from '@angular/core';
import { InjectionToken, Directive, EventEmitter, inject, Injector, afterNextRender, ANIMATION_MODULE_TYPE, booleanAttribute, numberAttribute, Component, ChangeDetectionStrategy, ViewEncapsulation, forwardRef, Optional, Host, Inject, Attribute, ContentChild, Input, Output, Self, ContentChildren, NgModule } from '@angular/core';
import * as i2 from '@sbb-esta/angular/core';
import { _ErrorStateTracker, SbbCommonModule } from '@sbb-esta/angular/core';
import * as i1 from '@sbb-esta/angular/icon';
import { SbbIconModule } from '@sbb-esta/angular/icon';
import { Subject, merge } from 'rxjs';
import * as i1$2 from '@angular/forms';
import * as i1$1 from '@sbb-esta/angular/autocomplete';
import { filter, takeUntil, startWith } from 'rxjs/operators';
import { FocusKeyManager } from '@angular/cdk/a11y';
import { SbbFormFieldControl } from '@sbb-esta/angular/form-field';

/**
 * Token used to provide a `SbbChipList` to `SbbChip`.
 * Used primarily to avoid circular imports between `SbbChipList` and `SbbChip`.
 */
const SBB_CHIP_LIST = new InjectionToken('SBB_CHIP_LIST');
/**
 * Token used to provide a `SbbChip` to `SbbChipList`.
 * Used primarily to avoid circular imports between `SbbChipList` and `SbbChip`.
 */
const SBB_CHIP = new InjectionToken('SBB_CHIP');

/**
 * Injection token that can be used to reference instances of `SbbChipRemove`. It serves as
 * alternative token to the actual `SbbChipRemove` class which could cause unnecessary
 * retention of the class and its directive metadata.
 */
const SBB_CHIP_REMOVE = new InjectionToken('SbbChipRemove');
/**
 * Injection token that can be used to reference instances of `SbbChipTrailingIcon`. It serves as
 * alternative token to the actual `SbbChipTrailingIcon` class which could cause unnecessary
 * retention of the class and its directive metadata.
 */
const SBB_CHIP_TRAILING_ICON = new InjectionToken('SbbChipTrailingIcon');
/**
 * Dummy directive to add CSS class to chip trailing icon.
 * @docs-private
 */
class SbbChipTrailingIcon {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbChipTrailingIcon, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.7", type: SbbChipTrailingIcon, isStandalone: true, selector: "sbb-chip-trailing-icon, [sbbChipTrailingIcon]", host: { classAttribute: "sbb-chip-trailing-icon" }, providers: [{ provide: SBB_CHIP_TRAILING_ICON, useExisting: SbbChipTrailingIcon }], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbChipTrailingIcon, decorators: [{
            type: Directive,
            args: [{
                    selector: 'sbb-chip-trailing-icon, [sbbChipTrailingIcon]',
                    host: { class: 'sbb-chip-trailing-icon' },
                    providers: [{ provide: SBB_CHIP_TRAILING_ICON, useExisting: SbbChipTrailingIcon }],
                    standalone: true,
                }]
        }] });
/**
 * Design styled Chip component. Used inside the SbbChipList component.
 */
class SbbChip {
    /** The value of the chip. Defaults to the content inside `<sbb-chip>` tags. */
    get value() {
        return this._value !== undefined ? this._value : this._elementRef.nativeElement.textContent;
    }
    set value(value) {
        this._value = value;
    }
    /** Whether the chip is disabled. */
    get disabled() {
        return this._chipListDisabled || this._disabled;
    }
    set disabled(value) {
        this._disabled = value;
    }
    constructor(_elementRef, _ngZone, _changeDetectorRef, _chipList, animationMode, tabIndex) {
        this._elementRef = _elementRef;
        this._ngZone = _ngZone;
        this._changeDetectorRef = _changeDetectorRef;
        this._chipList = _chipList;
        /** Whether the chip has focus. */
        this._hasFocus = false;
        /** Whether the chip list as a whole is disabled. */
        this._chipListDisabled = false;
        /** ARIA role that should be applied to the chip. */
        this.role = 'option';
        this._disabled = false;
        /**
         * Determines whether the chip displays the remove styling and emits (removed) events.
         */
        this.removable = true;
        /** Tab index of the chip. */
        this.tabIndex = -1;
        /** Emits when the chip is focused. */
        this._onFocus = new Subject();
        /** Emits when the chip is blurred. */
        this._onBlur = new Subject();
        /** Emitted when the chip is destroyed. */
        this.destroyed = new EventEmitter();
        /**
         * Emitted when a chip is to be removed.
         *
         * If a FormControl (Array or Set) on the sbb-chip-list is present and no subscriber
         * listens to (removed), the input value will automatically be removed from
         * the FormControl collection.
         */
        this.removed = new EventEmitter();
        this._injector = inject(Injector);
        this._addHostClassName();
        this._animationsDisabled = animationMode === 'NoopAnimations';
        this.tabIndex = tabIndex != null ? parseInt(tabIndex, 10) || -1 : -1;
    }
    _addHostClassName() {
        const basicChipAttrName = 'sbb-basic-chip';
        const element = this._elementRef.nativeElement;
        if (element.hasAttribute(basicChipAttrName) ||
            element.tagName.toLowerCase() === basicChipAttrName) {
            element.classList.add(basicChipAttrName);
            return;
        }
        else {
            element.classList.add('sbb-standard-chip');
        }
    }
    ngOnDestroy() {
        this.destroyed.emit({ chip: this });
    }
    /** Allows for programmatic focusing of the chip. */
    focus() {
        if (!this._hasFocus) {
            this._elementRef.nativeElement.focus();
            this._onFocus.next({ chip: this });
        }
        this._hasFocus = true;
    }
    /**
     * Allows for programmatic removal of the chip. Called by the SbbChipList when the DELETE or
     * BACKSPACE keys are pressed.
     *
     * Informs any listeners of the removal request. Does not remove the chip from the DOM.
     */
    remove() {
        if (this.removable) {
            this._removeValueFromControl();
            this.removed.emit({ chip: this });
        }
    }
    _removeValueFromControl() {
        const control = this._chipList?.ngControl?.control;
        if (!control || this.removed.observers.length) {
            return;
        }
        const currentCollection = control.value;
        const isArray = Array.isArray(currentCollection);
        const isSet = currentCollection instanceof Set;
        if (isArray) {
            if (!currentCollection.includes(this.value)) {
                return;
            }
            const indexInChipList = this._chipList?.chips.toArray().indexOf(this);
            if (indexInChipList >= 0 && currentCollection[indexInChipList] === this.value) {
                control.patchValue(currentCollection.filter((val, index) => indexInChipList !== index));
            }
            else {
                // Filter by value can delete more than one entry at once, so only use this as a fallback.
                control.patchValue(currentCollection.filter((val) => val !== this.value));
            }
        }
        else if (isSet) {
            const newCurrentCollection = new Set(currentCollection);
            newCurrentCollection.delete(this.value);
            control.patchValue(newCurrentCollection);
        }
        if (isArray || isSet) {
            control.markAsDirty();
        }
    }
    /** Handles click events on the chip. */
    _handleClick(event) {
        if (this.disabled) {
            event.preventDefault();
        }
    }
    /** Handle custom key presses. */
    _handleKeydown(event) {
        if (this.disabled) {
            return;
        }
        switch (event.keyCode) {
            case DELETE:
            case BACKSPACE:
                // If we are removable, remove the focused chip
                this.remove();
                // Always prevent so page navigation does not occur
                event.preventDefault();
                break;
        }
    }
    _blur() {
        // When animations are enabled, Angular may end up removing the chip from the DOM a little
        // earlier than usual, causing it to be blurred and throwing off the logic in the chip list
        // that moves focus not the next item. To work around the issue, we defer marking the chip
        // as not focused until after the next render.
        afterNextRender(() => this._ngZone.run(() => {
            this._hasFocus = false;
            this._onBlur.next({ chip: this });
        }), { injector: this._injector });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbChip, deps: [{ token: i0.ElementRef }, { token: i0.NgZone }, { token: i0.ChangeDetectorRef, optional: true }, { token: SBB_CHIP_LIST, host: true, optional: true }, { token: ANIMATION_MODULE_TYPE, optional: true }, { token: 'tabindex', attribute: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "17.3.7", type: SbbChip, isStandalone: true, selector: "sbb-basic-chip, [sbb-basic-chip], sbb-chip, [sbb-chip]", inputs: { role: "role", value: "value", disabled: ["disabled", "disabled", booleanAttribute], removable: ["removable", "removable", booleanAttribute], tabIndex: ["tabIndex", "tabIndex", (value) => (value == null ? undefined : numberAttribute(value))] }, outputs: { destroyed: "destroyed", removed: "removed" }, host: { listeners: { "click": "_handleClick($event)", "keydown": "_handleKeydown($event)", "focus": "focus()", "blur": "_blur()" }, properties: { "attr.tabindex": "disabled ? null : tabIndex", "attr.role": "role", "class.sbb-chip-with-trailing-icon": "trailingIcon || removable", "class.sbb-chip-disabled": "disabled", "attr.disabled": "disabled || null", "attr.aria-disabled": "disabled.toString()" }, classAttribute: "sbb-chip sbb-icon-scaled sbb-button-reset-frameless" }, providers: [{ provide: SBB_CHIP, useExisting: SbbChip }], queries: [{ propertyName: "trailingIcon", first: true, predicate: SBB_CHIP_TRAILING_ICON, descendants: true }, { propertyName: "removeIcon", first: true, predicate: SBB_CHIP_REMOVE, descendants: true }], exportAs: ["sbbChip"], ngImport: i0, template: ` <ng-content></ng-content>
    @if (removable && !removeIcon) {
      <sbb-icon sbbChipRemove svgIcon="cross-small"> </sbb-icon>
    }`, isInline: true, dependencies: [{ kind: "ngmodule", type: i0.forwardRef(() => SbbIconModule) }, { kind: "component", type: i0.forwardRef(() => i1.SbbIcon), selector: "sbb-icon", inputs: ["color", "inline", "svgIcon", "fontSet", "fontIcon", "id"], exportAs: ["sbbIcon"] }, { kind: "directive", type: i0.forwardRef(() => SbbChipRemove), selector: "[sbbChipRemove]" }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbChip, decorators: [{
            type: Component,
            args: [{
                    selector: `sbb-basic-chip, [sbb-basic-chip], sbb-chip, [sbb-chip]`,
                    exportAs: 'sbbChip',
                    template: ` <ng-content></ng-content>
    @if (removable && !removeIcon) {
      <sbb-icon sbbChipRemove svgIcon="cross-small"> </sbb-icon>
    }`,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    encapsulation: ViewEncapsulation.None,
                    host: {
                        class: 'sbb-chip sbb-icon-scaled sbb-button-reset-frameless',
                        '[attr.tabindex]': 'disabled ? null : tabIndex',
                        '[attr.role]': 'role',
                        '[class.sbb-chip-with-trailing-icon]': 'trailingIcon || removable',
                        '[class.sbb-chip-disabled]': 'disabled',
                        '[attr.disabled]': 'disabled || null',
                        '[attr.aria-disabled]': 'disabled.toString()',
                        '(click)': '_handleClick($event)',
                        '(keydown)': '_handleKeydown($event)',
                        '(focus)': 'focus()',
                        '(blur)': '_blur()',
                    },
                    providers: [{ provide: SBB_CHIP, useExisting: SbbChip }],
                    standalone: true,
                    imports: [SbbIconModule, forwardRef(() => SbbChipRemove)],
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.NgZone }, { type: i0.ChangeDetectorRef, decorators: [{
                    type: Optional
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Host
                }, {
                    type: Inject,
                    args: [SBB_CHIP_LIST]
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [ANIMATION_MODULE_TYPE]
                }] }, { type: undefined, decorators: [{
                    type: Attribute,
                    args: ['tabindex']
                }] }], propDecorators: { trailingIcon: [{
                type: ContentChild,
                args: [SBB_CHIP_TRAILING_ICON]
            }], removeIcon: [{
                type: ContentChild,
                args: [SBB_CHIP_REMOVE]
            }], role: [{
                type: Input
            }], value: [{
                type: Input
            }], disabled: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }], removable: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }], tabIndex: [{
                type: Input,
                args: [{
                        transform: (value) => (value == null ? undefined : numberAttribute(value)),
                    }]
            }], destroyed: [{
                type: Output
            }], removed: [{
                type: Output
            }] } });
/**
 * Applies proper (click) support and adds styling for use with the Sbb Design "cancel"
 *
 * You *may* use a custom icon, but you may need to override the `sbb-chip-remove` positioning
 * styles to properly center the icon within the chip.
 */
class SbbChipRemove {
    constructor(_parentChip, elementRef) {
        this._parentChip = _parentChip;
        if (elementRef.nativeElement.nodeName === 'BUTTON') {
            elementRef.nativeElement.setAttribute('type', 'button');
        }
    }
    /** Calls the parent chip's public `remove()` method if applicable. */
    _handleClick(event) {
        const parentChip = this._parentChip;
        if (parentChip.removable && !parentChip.disabled) {
            parentChip.remove();
        }
        // We need to stop event propagation because otherwise the event will bubble up to the
        // form field and cause the `onContainerClick` method to be invoked. This method would then
        // reset the focused chip that has been focused after chip removal. Usually the parent
        // the parent click listener of the `SbbChip` would prevent propagation, but it can happen
        // that the chip is being removed before the event bubbles up.
        event.stopPropagation();
        event.preventDefault();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbChipRemove, deps: [{ token: SbbChip }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.7", type: SbbChipRemove, isStandalone: true, selector: "[sbbChipRemove]", host: { listeners: { "click": "_handleClick($event)" }, classAttribute: "sbb-chip-remove sbb-chip-trailing-icon" }, providers: [{ provide: SBB_CHIP_REMOVE, useExisting: SbbChipRemove }], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbChipRemove, decorators: [{
            type: Directive,
            args: [{
                    selector: '[sbbChipRemove]',
                    host: {
                        class: 'sbb-chip-remove sbb-chip-trailing-icon',
                        '(click)': '_handleClick($event)',
                    },
                    providers: [{ provide: SBB_CHIP_REMOVE, useExisting: SbbChipRemove }],
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: SbbChip }, { type: i0.ElementRef }] });

/** Injection token to be used to override the default options for the chips module. */
const SBB_CHIPS_DEFAULT_OPTIONS = new InjectionToken('sbb-chips-default-options');

// Increasing integer for generating unique ids.
let nextUniqueId$1 = 0;
/**
 * Directive that adds chip-specific behaviors to an input element inside `<sbb-form-field>`.
 * May be placed inside or outside of an `<sbb-chip-list>`.
 */
class SbbChipInput {
    /** Register input for chip list */
    set chipList(value) {
        if (value) {
            this._chipList = value;
            this._chipList.registerInput(this);
        }
    }
    /** Whether the input is disabled. */
    get disabled() {
        return this._disabled || (this._chipList && this._chipList.disabled);
    }
    set disabled(value) {
        this._disabled = value;
    }
    /** Whether the input is empty. */
    get empty() {
        return !this.inputElement.value;
    }
    constructor(_elementRef, _defaultOptions, autocompleteTrigger, chipList, _ngControl) {
        this._elementRef = _elementRef;
        this._defaultOptions = _defaultOptions;
        this.autocompleteTrigger = autocompleteTrigger;
        this._ngControl = _ngControl;
        /** Whether the control is focused. */
        this.focused = false;
        this._destroyed = new Subject();
        /**
         * Whether the chipEnd event will be emitted when the input is blurred.
         */
        this.addOnBlur = false;
        /**
         * The list of key codes that will trigger a chipEnd event.
         *
         * Defaults to `[ENTER]`.
         */
        this.separatorKeyCodes = this._defaultOptions.separatorKeyCodes;
        /**
         * Emitted when a chip is to be added.
         *
         * If a FormControl (Array or Set) on the sbb-chip-list is present and no subscriber
         * listens to (sbbChipInputTokenEnd), the input value will automatically be added to
         * the FormControl collection.
         */
        this.chipEnd = new EventEmitter();
        /** The input's placeholder text. */
        this.placeholder = '';
        /** Unique id for the input. */
        this.id = `sbb-chip-list-input-${nextUniqueId$1++}`;
        this._disabled = false;
        this.inputElement = this._elementRef.nativeElement;
        if (chipList) {
            this.chipList = chipList;
        }
    }
    ngOnChanges() {
        this._chipList.stateChanges.next();
    }
    ngOnDestroy() {
        this.chipEnd.complete();
        this._destroyed.next();
        this._destroyed.complete();
    }
    ngAfterContentInit() {
        this._focusLastChipOnBackspace = this.empty;
        // Try to add autocomplete selected value to FormControl.
        // Skip this part, if there are already other observers which might have its own logic.
        if (this.autocompleteTrigger && this.autocompleteTrigger.autocomplete) {
            this.autocompleteTrigger.autocomplete.optionSelected
                .pipe(filter(() => this.autocompleteTrigger?.autocomplete.optionSelected.observers.length === 1), takeUntil(this._destroyed))
                .subscribe((selectedEvent) => {
                this._addValueToControl(selectedEvent.option.viewValue);
                this.chipEnd.emit({
                    value: selectedEvent.option.viewValue,
                    chipInput: this,
                });
            });
        }
    }
    /** Utility method to make host definition/tests more clear. */
    _keydown(event) {
        if (event) {
            // Allow the user's focus to escape when they're tabbing forward. Note that we don't
            // want to do this when going backwards, because focus should go back to the first chip.
            if (event.keyCode === TAB && !hasModifierKey(event, 'shiftKey')) {
                this._chipList._allowFocusEscape();
            }
            // To prevent the user from accidentally deleting chips when pressing BACKSPACE continuously,
            // We focus the last chip on backspace only after the user has released the backspace button,
            // and the input is empty (see behaviour in _keyup)
            if (event.keyCode === BACKSPACE && this._focusLastChipOnBackspace) {
                this._chipList._keyManager.setLastItemActive();
                event.preventDefault();
                return;
            }
            else {
                this._focusLastChipOnBackspace = false;
            }
        }
        // If an autocomplete is open, we have to ensure that the (optionSelected) listener
        // is called before this._emitChipEnd is called. The (optionSelected) listener clears
        // the input and therefore we can prevent adding the current chip input value to the formControl.
        // If not using this logic, the current chip input value will be added together
        // with the selected autocomplete value.
        if (this.autocompleteTrigger?.autocomplete.isOpen) {
            Promise.resolve().then(() => this._emitChipEnd(event));
        }
        else {
            this._emitChipEnd(event);
        }
    }
    /**
     * Pass events to the keyboard manager. Available here for tests.
     */
    _keyup(event) {
        // Allow user to move focus to chips next time he presses backspace
        if (!this._focusLastChipOnBackspace && event.keyCode === BACKSPACE && this.empty) {
            this._focusLastChipOnBackspace = true;
            event.preventDefault();
        }
    }
    /** Checks to see if the blur should emit the (chipEnd) event. */
    _blur() {
        if (this.addOnBlur) {
            this._emitChipEnd();
        }
        this.focused = false;
        // Blur the chip list if it is not focused
        if (!this._chipList.focused) {
            this._chipList._blur();
        }
        this._chipList.stateChanges.next();
    }
    _focus() {
        this.focused = true;
        this._chipList.stateChanges.next();
    }
    /** Checks to see if the (chipEnd) event needs to be emitted. */
    _emitChipEnd(event) {
        if (!this.inputElement.value && !!event) {
            this._chipList._keydown(event);
        }
        if (!event || this._isSeparatorKey(event)) {
            this._addValueToControl(this.inputElement.value);
            this.chipEnd.emit({
                value: this.inputElement.value,
                chipInput: this,
            });
            event?.preventDefault();
        }
    }
    _addValueToControl(inputValue) {
        if (!this._chipList?.ngControl?.control || inputValue === '' || this.chipEnd.observers.length) {
            return;
        }
        const control = this._chipList.ngControl.control;
        const currentCollection = control.value;
        const isArray = Array.isArray(currentCollection);
        const isSet = currentCollection instanceof Set;
        if (isArray) {
            control.patchValue([...currentCollection, inputValue]);
        }
        else if (isSet) {
            control.patchValue(new Set([...currentCollection, inputValue]));
        }
        if (isArray || isSet) {
            this._chipList.ngControl.control.markAsDirty();
            this.clear();
        }
    }
    _onInput() {
        // Let chip list know whenever the value changes.
        this._chipList.stateChanges.next();
    }
    /** Focuses the input. */
    focus(options) {
        this.inputElement.focus(options);
    }
    /** Clears the input */
    clear() {
        this.inputElement.value = '';
        this._ngControl?.control?.setValue(null);
        this._focusLastChipOnBackspace = true;
    }
    /** Checks whether a keycode is one of the configured separators. */
    _isSeparatorKey(event) {
        return !hasModifierKey(event) && new Set(this.separatorKeyCodes).has(event.keyCode);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbChipInput, deps: [{ token: i0.ElementRef }, { token: SBB_CHIPS_DEFAULT_OPTIONS }, { token: i1$1.SbbAutocompleteTrigger, optional: true, self: true }, { token: SBB_CHIP_LIST, host: true, optional: true }, { token: i1$2.NgControl, optional: true, self: true }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "16.1.0", version: "17.3.7", type: SbbChipInput, isStandalone: true, selector: "input[sbbChipInputFor], input[sbbChipInput]", inputs: { chipList: ["sbbChipInputFor", "chipList"], addOnBlur: ["sbbChipInputAddOnBlur", "addOnBlur", booleanAttribute], separatorKeyCodes: ["sbbChipInputSeparatorKeyCodes", "separatorKeyCodes"], placeholder: "placeholder", id: "id", disabled: ["disabled", "disabled", booleanAttribute] }, outputs: { chipEnd: "sbbChipInputTokenEnd" }, host: { listeners: { "keydown": "_keydown($event)", "keyup": "_keyup($event)", "blur": "_blur()", "focus": "_focus()", "input": "_onInput()" }, properties: { "id": "id", "attr.disabled": "disabled || null", "attr.placeholder": "placeholder || null", "attr.aria-invalid": "_chipList && _chipList.ngControl ? _chipList.ngControl.invalid : null", "attr.aria-required": "_chipList && _chipList.required || null" }, classAttribute: "sbb-chip-input sbb-input-element" }, exportAs: ["sbbChipInput", "sbbChipInputFor"], usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbChipInput, decorators: [{
            type: Directive,
            args: [{
                    selector: 'input[sbbChipInputFor], input[sbbChipInput]',
                    exportAs: 'sbbChipInput, sbbChipInputFor',
                    host: {
                        class: 'sbb-chip-input sbb-input-element',
                        '(keydown)': '_keydown($event)',
                        '(keyup)': '_keyup($event)',
                        '(blur)': '_blur()',
                        '(focus)': '_focus()',
                        '(input)': '_onInput()',
                        '[id]': 'id',
                        '[attr.disabled]': 'disabled || null',
                        '[attr.placeholder]': 'placeholder || null',
                        '[attr.aria-invalid]': '_chipList && _chipList.ngControl ? _chipList.ngControl.invalid : null',
                        '[attr.aria-required]': '_chipList && _chipList.required || null',
                    },
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [SBB_CHIPS_DEFAULT_OPTIONS]
                }] }, { type: i1$1.SbbAutocompleteTrigger, decorators: [{
                    type: Self
                }, {
                    type: Optional
                }] }, { type: undefined, decorators: [{
                    type: Host
                }, {
                    type: Optional
                }, {
                    type: Inject,
                    args: [SBB_CHIP_LIST]
                }] }, { type: i1$2.NgControl, decorators: [{
                    type: Self
                }, {
                    type: Optional
                }] }], propDecorators: { chipList: [{
                type: Input,
                args: ['sbbChipInputFor']
            }], addOnBlur: [{
                type: Input,
                args: [{ alias: 'sbbChipInputAddOnBlur', transform: booleanAttribute }]
            }], separatorKeyCodes: [{
                type: Input,
                args: ['sbbChipInputSeparatorKeyCodes']
            }], chipEnd: [{
                type: Output,
                args: ['sbbChipInputTokenEnd']
            }], placeholder: [{
                type: Input
            }], id: [{
                type: Input
            }], disabled: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }] } });

// Increasing integer for generating unique ids for chip-list components.
let nextUniqueId = 0;
/**
 * A design chips component (named ChipList for its similarity to the List component).
 */
class SbbChipList {
    /** The ARIA role applied to the chip list. */
    get role() {
        if (this._explicitRole) {
            return this._explicitRole;
        }
        return this.empty ? null : 'listbox';
    }
    set role(role) {
        this._explicitRole = role;
    }
    /** An object used to control when error messages are shown. */
    get errorStateMatcher() {
        return this._errorStateTracker.matcher;
    }
    set errorStateMatcher(value) {
        this._errorStateTracker.matcher = value;
    }
    /**
     * Implemented as part of SbbFormFieldControl.
     * @docs-private
     */
    get value() {
        return this._value;
    }
    set value(value) {
        this.writeValue(value);
        this._value = value;
    }
    /**
     * Implemented as part of SbbFormFieldControl.
     * @docs-private
     */
    get id() {
        return this._chipInput ? this._chipInput.id : this._uid;
    }
    /**
     * Implemented as part of SbbFormFieldControl.
     * @docs-private
     */
    get placeholder() {
        return this._chipInput ? this._chipInput.placeholder : this._placeholder;
    }
    set placeholder(value) {
        this._placeholder = value;
        this.stateChanges.next();
    }
    /** Whether any chips or the sbbChipInput inside of this chip-list has focus. */
    get focused() {
        return (this._chipInput && this._chipInput.focused) || this._hasFocusedChip();
    }
    /**
     * Implemented as part of SbbFormFieldControl.
     * @docs-private
     */
    get empty() {
        return (!this._chipInput || this._chipInput.empty) && (!this.chips || this.chips.length === 0);
    }
    /**
     * Implemented as part of SbbFormFieldControl.
     * @docs-private
     */
    get disabled() {
        return this.ngControl ? !!this.ngControl.disabled : this._disabled;
    }
    set disabled(value) {
        this._disabled = value;
        this._syncChipsState();
    }
    set tabIndex(value) {
        this._userTabIndex = value;
        this._tabIndex = value;
    }
    /** Combined stream of all of the child chips' focus change events. */
    get chipFocusChanges() {
        return merge(...this.chips.map((chip) => chip._onFocus));
    }
    /** Combined stream of all of the child chips' blur change events. */
    get chipBlurChanges() {
        return merge(...this.chips.map((chip) => chip._onBlur));
    }
    /** Combined stream of all of the child chips' remove change events. */
    get chipRemoveChanges() {
        return merge(...this.chips.map((chip) => chip.destroyed));
    }
    /** Whether the chip grid is in an error state. */
    get errorState() {
        return this._errorStateTracker.errorState;
    }
    set errorState(value) {
        this._errorStateTracker.errorState = value;
    }
    constructor(_elementRef, _changeDetectorRef, parentForm, parentFormGroup, defaultErrorStateMatcher, 
    /** @docs-private */
    ngControl) {
        this._elementRef = _elementRef;
        this._changeDetectorRef = _changeDetectorRef;
        this.ngControl = ngControl;
        /**
         * Implemented as part of SbbFormFieldControl.
         * @docs-private
         */
        this.controlType = 'sbb-chip-list';
        /**
         * When a chip is destroyed, we store the index of the destroyed chip until the chips
         * query list notifies about the update. This is necessary because we cannot determine an
         * appropriate chip that should receive focus until the array of chips updated completely.
         */
        this._lastDestroyedChipIndex = null;
        /** Subject that emits when the component has been destroyed. */
        this._destroyed = new Subject();
        /** Uid of the chip list */
        this._uid = `sbb-chip-list-${nextUniqueId++}`;
        /** Tab index for the chip list. */
        this._tabIndex = 0;
        /**
         * User defined tab index.
         * When it is not null, use user defined tab index. Otherwise use _tabIndex
         */
        this._userTabIndex = null;
        /** Function when touched */
        this._onTouched = () => { };
        /** Function when changed */
        this._onChange = () => { };
        /**
         * Implemented as part of SbbFormFieldControl.
         * @docs-private
         */
        this.required = false;
        this._disabled = false;
        /** Orientation of the chip list. */
        this.ariaOrientation = 'horizontal';
        /**
         * Event that emits whenever the raw value of the chip-list changes. This is here primarily
         * to facilitate the two-way binding for the `value` input.
         * @docs-private
         */
        this.valueChange = new EventEmitter();
        /**
         * Emits whenever the component state changes and should cause the parent
         * form-field to update. Implemented as part of `SbbFormFieldControl`.
         * @docs-private
         */
        this.stateChanges = new Subject();
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }
        this._errorStateTracker = new _ErrorStateTracker(defaultErrorStateMatcher, ngControl, parentFormGroup, parentForm, this.stateChanges);
    }
    ngAfterContentInit() {
        this._keyManager = new FocusKeyManager(this.chips)
            .withWrap()
            .withVerticalOrientation()
            .withHomeAndEnd()
            .withHorizontalOrientation('ltr');
        this._keyManager.tabOut.subscribe(() => this._allowFocusEscape());
        // When the list changes, re-subscribe
        this.chips.changes.pipe(startWith(null), takeUntil(this._destroyed)).subscribe(() => {
            if (this.disabled) {
                // Since this happens after the content has been
                // checked, we need to defer it to the next tick.
                Promise.resolve().then(() => {
                    this._syncChipsState();
                });
            }
            this._resetChips();
            // Check to see if we need to update our tab index
            this._updateTabIndex();
            // Check to see if we have a destroyed chip and need to refocus
            this._updateFocusForDestroyedChips();
            // Reposition autocomplete if present and open because size of list can be changed
            if (this._chipInput?.autocompleteTrigger?.autocomplete.isOpen) {
                // We have to delay position update after DOM is rendered
                Promise.resolve().then(() => {
                    this._chipInput?.autocompleteTrigger?.updatePosition();
                });
            }
            this.stateChanges.next();
        });
    }
    ngOnInit() {
        this.stateChanges.next();
    }
    ngDoCheck() {
        if (this.ngControl) {
            // We need to re-evaluate this on every change detection cycle, because there are some
            // error triggers that we can't subscribe to (e.g. parent form submissions). This means
            // that whatever logic is in here has to be super lean or we risk destroying the performance.
            this.updateErrorState();
            if (this.ngControl.disabled !== this._disabled) {
                this.disabled = !!this.ngControl.disabled;
            }
        }
    }
    ngOnDestroy() {
        this._keyManager?.destroy();
        this._destroyed.next();
        this._destroyed.complete();
        this.stateChanges.complete();
        this._dropSubscriptions();
    }
    /** Associates an HTML input element with this chip list. */
    registerInput(inputElement) {
        this._chipInput = inputElement;
        // We use this attribute to match the chip list to its input in test harnesses.
        // Set the attribute directly here to avoid "changed after checked" errors.
        this._elementRef.nativeElement.setAttribute('data-sbb-chip-input', inputElement.id);
    }
    /**
     * Implemented as part of SbbFormFieldControl.
     * @docs-private
     */
    setDescribedByIds(ids) {
        if (ids.length) {
            this._elementRef.nativeElement.setAttribute('aria-describedby', ids.join(' '));
        }
        else {
            this._elementRef.nativeElement.removeAttribute('aria-describedby');
        }
    }
    // Implemented as part of ControlValueAccessor.
    writeValue(value) { }
    // Implemented as part of ControlValueAccessor.
    registerOnChange(fn) {
        this._onChange = fn;
    }
    // Implemented as part of ControlValueAccessor.
    registerOnTouched(fn) {
        this._onTouched = fn;
    }
    // Implemented as part of ControlValueAccessor.
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
        this.stateChanges.next();
    }
    /**
     * Implemented as part of SbbFormFieldControl.
     * @docs-private
     */
    onContainerClick(event) {
        if (!this._originatesFromChip(event)) {
            this.focus();
        }
    }
    /**
     * Focuses the first non-disabled chip in this chip list, or the associated input when there
     * are no eligible chips.
     */
    focus(options) {
        if (this.disabled) {
            return;
        }
        // Focus on first element if there's no chipInput inside chip-list
        if (this._chipInput && this._chipInput.focused) {
            // do nothing
        }
        else if (this.chips.length > 0) {
            this._keyManager.setFirstItemActive();
            this.stateChanges.next();
        }
        else {
            this._focusInput(options);
            this.stateChanges.next();
        }
    }
    /** Attempt to focus an input if we have one. */
    _focusInput(options) {
        if (this._chipInput) {
            this._chipInput.focus(options);
        }
    }
    /**
     * Pass events to the keyboard manager. Available here for tests.
     */
    _keydown(event) {
        const target = event.target;
        if (target && target.classList.contains('sbb-chip')) {
            this._keyManager.onKeydown(event);
            this.stateChanges.next();
        }
    }
    /**
     * Check the tab index as you should not be allowed to focus an empty list.
     */
    _updateTabIndex() {
        // If we have 0 chips, we should not allow keyboard focus
        this._tabIndex = this._userTabIndex || (this.chips.length === 0 ? -1 : 0);
    }
    /**
     * If the amount of chips changed, we need to update the
     * key manager state and focus the next closest chip.
     */
    _updateFocusForDestroyedChips() {
        // Move focus to the closest chip. If no other chips remain, focus the chip-list itself.
        if (this._lastDestroyedChipIndex != null) {
            if (this.chips.length) {
                const newChipIndex = Math.min(this._lastDestroyedChipIndex, this.chips.length - 1);
                this._keyManager.setActiveItem(newChipIndex);
            }
            else {
                this.focus();
            }
        }
        this._lastDestroyedChipIndex = null;
    }
    /**
     * Utility to ensure all indexes are valid.
     *
     * @param index The index to be checked.
     * @returns True if the index is valid for our list of chips.
     */
    _isValidIndex(index) {
        return index >= 0 && index < this.chips.length;
    }
    /** Refreshes the error state of the chip grid. */
    updateErrorState() {
        this._errorStateTracker.updateErrorState();
    }
    /** When blurred, mark the field as touched when focus moved outside the chip list. */
    _blur() {
        if (!this._hasFocusedChip()) {
            this._keyManager.setActiveItem(-1);
        }
        if (!this.disabled) {
            if (this._chipInput) {
                // If there's a chip input, we should check whether the focus moved to chip input.
                // If the focus is not moved to chip input, mark the field as touched. If the focus moved
                // to chip input, do nothing.
                // Timeout is needed to wait for the focus() event trigger on chip input.
                setTimeout(() => {
                    if (!this.focused) {
                        this._markAsTouched();
                    }
                });
            }
            else {
                // If there's no chip input, then mark the field as touched.
                this._markAsTouched();
            }
        }
    }
    /** Mark the field as touched */
    _markAsTouched() {
        this._onTouched();
        this._changeDetectorRef.markForCheck();
        this.stateChanges.next();
    }
    /**
     * Removes the `tabindex` from the chip list and resets it back afterwards, allowing the
     * user to tab out of it. This prevents the list from capturing focus and redirecting
     * it back to the first chip, creating a focus trap, if it user tries to tab away.
     */
    _allowFocusEscape() {
        if (this._tabIndex !== -1) {
            this._tabIndex = -1;
            setTimeout(() => {
                this._tabIndex = this._userTabIndex || 0;
                this._changeDetectorRef.markForCheck();
            });
        }
    }
    _resetChips() {
        this._dropSubscriptions();
        this._listenToChipsFocus();
        this._listenToChipsRemoved();
    }
    _dropSubscriptions() {
        if (this._chipFocusSubscription) {
            this._chipFocusSubscription.unsubscribe();
            this._chipFocusSubscription = null;
        }
        if (this._chipBlurSubscription) {
            this._chipBlurSubscription.unsubscribe();
            this._chipBlurSubscription = null;
        }
        if (this._chipRemoveSubscription) {
            this._chipRemoveSubscription.unsubscribe();
            this._chipRemoveSubscription = null;
        }
    }
    /** Listens to user-generated selection events on each chip. */
    _listenToChipsFocus() {
        this._chipFocusSubscription = this.chipFocusChanges.subscribe((event) => {
            const chipIndex = this.chips.toArray().indexOf(event.chip);
            if (this._isValidIndex(chipIndex)) {
                this._keyManager.updateActiveItem(chipIndex);
            }
            this.stateChanges.next();
        });
        this._chipBlurSubscription = this.chipBlurChanges.subscribe(() => {
            this._blur();
            this.stateChanges.next();
        });
    }
    _listenToChipsRemoved() {
        this._chipRemoveSubscription = this.chipRemoveChanges.subscribe((event) => {
            const chip = event.chip;
            const chipIndex = this.chips.toArray().indexOf(event.chip);
            // In case the chip that will be removed is currently focused, we temporarily store
            // the index in order to be able to determine an appropriate sibling chip that will
            // receive focus.
            if (this._isValidIndex(chipIndex) && chip._hasFocus) {
                this._lastDestroyedChipIndex = chipIndex;
            }
        });
    }
    /** Checks whether an event comes from inside a chip element. */
    _originatesFromChip(event) {
        let currentElement = event.target;
        while (currentElement && currentElement !== this._elementRef.nativeElement) {
            if (currentElement.classList.contains('sbb-chip')) {
                return true;
            }
            currentElement = currentElement.parentElement;
        }
        return false;
    }
    /** Checks whether any of the chips is focused. */
    _hasFocusedChip() {
        return this.chips && this.chips.some((chip) => chip._hasFocus);
    }
    /** Syncs the list's state with the individual chips. */
    _syncChipsState() {
        if (this.chips) {
            this.chips.forEach((chip) => {
                chip._chipListDisabled = this._disabled;
            });
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbChipList, deps: [{ token: i0.ElementRef }, { token: i0.ChangeDetectorRef }, { token: i1$2.NgForm, optional: true }, { token: i1$2.FormGroupDirective, optional: true }, { token: i2.SbbErrorStateMatcher }, { token: i1$2.NgControl, optional: true, self: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "16.1.0", version: "17.3.7", type: SbbChipList, isStandalone: true, selector: "sbb-chip-list", inputs: { role: "role", userAriaDescribedBy: ["aria-describedby", "userAriaDescribedBy"], errorStateMatcher: "errorStateMatcher", value: "value", required: ["required", "required", booleanAttribute], placeholder: "placeholder", disabled: ["disabled", "disabled", booleanAttribute], ariaOrientation: ["aria-orientation", "ariaOrientation"], tabIndex: "tabIndex" }, outputs: { valueChange: "valueChange" }, host: { listeners: { "focus": "focus()", "blur": "_blur()", "keydown": "_keydown($event)" }, properties: { "attr.tabindex": "disabled ? null : _tabIndex", "attr.aria-required": "role ? required : null", "attr.aria-disabled": "disabled.toString()", "attr.aria-invalid": "errorState", "attr.role": "role", "class.sbb-chip-list-disabled": "disabled", "class.sbb-disabled": "disabled", "class.sbb-chip-list-invalid": "errorState", "class.sbb-chip-list-required": "required", "class.sbb-expanded": "_chipInput?.autocompleteTrigger?.autocomplete?.isOpen", "class.sbb-focused": "focused", "class.sbb-input-element": "_chipInput", "attr.aria-orientation": "ariaOrientation", "id": "_uid" }, classAttribute: "sbb-chip-list" }, providers: [
            { provide: SbbFormFieldControl, useExisting: SbbChipList },
            { provide: SBB_CHIP_LIST, useExisting: SbbChipList },
        ], queries: [{ propertyName: "chips", predicate: SBB_CHIP, descendants: true }], exportAs: ["sbbChipList"], ngImport: i0, template: `<ng-content></ng-content>`, isInline: true, styles: [".sbb-chip-list{display:flex;flex-direction:row;flex-wrap:wrap;align-items:center;background:rgba(0,0,0,0);padding:0}.sbb-input-with-open-panel .sbb-chip-list{border-color:var(--sbb-form-input-color)}.sbb-chip-list .cdk-drag{cursor:grab}.sbb-chip-list.cdk-drop-list-dragging{cursor:grabbing}:is(.sbb-chip-input,.sbb-form-field .sbb-chip-input){max-width:100%;border:none;width:var(--sbb-chips-input-width);flex:1 0 var(--sbb-chips-input-width);min-height:calc(var(--sbb-chips-list-height) - 2*var(--sbb-border-width))}.sbb-chip-list-disabled :is(.sbb-chip-input,.sbb-form-field .sbb-chip-input){display:none}:where(html.sbb-lean) :is(.sbb-chip-input,.sbb-form-field .sbb-chip-input){padding-bottom:.4rem}.sbb-standard-chip{overflow:hidden;white-space:nowrap;text-overflow:ellipsis;font-size:var(--sbb-font-size);line-height:1;height:var(--sbb-chips-chip-height);border-radius:var(--sbb-chips-chip-height);color:var(--sbb-color-weak-accent);-webkit-text-fill-color:var(--sbb-color-weak-accent);background-color:var(--sbb-chips-background-color-active);max-width:100%;margin:var(--sbb-chips-chip-margin-vertical);padding:calc(.5*(var(--sbb-chips-chip-height) - var(--sbb-font-size) - 0.0666666667rem*var(--sbb-scaling-factor))) calc(0.9333333333rem*var(--sbb-scaling-factor))}.sbb-standard-chip:not(.sbb-chip-disabled):is(:hover,:focus){background-color:var(--sbb-chips-background-color-hover)}.sbb-standard-chip.sbb-chip-disabled{background-color:var(--sbb-chips-background-color-disabled);opacity:var(--sbb-chips-opacity)}.sbb-standard-chip.sbb-chip-disabled :is(.sbb-chip-remove,.sbb-chip-trailing-icon){cursor:default}.sbb-standard-chip.sbb-chip-with-trailing-icon{padding-right:calc(0.2666666667rem*var(--sbb-scaling-factor) + var(--sbb-icon-size-default) + var(--sbb-chips-chip-padding-horizontal));position:relative}.sbb-standard-chip :is(.sbb-chip-remove,.sbb-chip-trailing-icon){position:absolute;top:50%;transform:translateY(-50%);cursor:pointer;margin-left:var(--sbb-chips-chip-padding-horizontal);right:var(--sbb-chips-chip-padding-horizontal)}"], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbChipList, decorators: [{
            type: Component,
            args: [{ selector: 'sbb-chip-list', template: `<ng-content></ng-content>`, exportAs: 'sbbChipList', host: {
                        class: 'sbb-chip-list',
                        '[attr.tabindex]': 'disabled ? null : _tabIndex',
                        '[attr.aria-required]': 'role ? required : null',
                        '[attr.aria-disabled]': 'disabled.toString()',
                        '[attr.aria-invalid]': 'errorState',
                        '[attr.role]': 'role',
                        '[class.sbb-chip-list-disabled]': 'disabled',
                        '[class.sbb-disabled]': 'disabled',
                        '[class.sbb-chip-list-invalid]': 'errorState',
                        '[class.sbb-chip-list-required]': 'required',
                        '[class.sbb-expanded]': '_chipInput?.autocompleteTrigger?.autocomplete?.isOpen',
                        '[class.sbb-focused]': 'focused',
                        '[class.sbb-input-element]': '_chipInput',
                        '[attr.aria-orientation]': 'ariaOrientation',
                        '(focus)': 'focus()',
                        '(blur)': '_blur()',
                        '(keydown)': '_keydown($event)',
                        '[id]': '_uid',
                    }, providers: [
                        { provide: SbbFormFieldControl, useExisting: SbbChipList },
                        { provide: SBB_CHIP_LIST, useExisting: SbbChipList },
                    ], encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, standalone: true, styles: [".sbb-chip-list{display:flex;flex-direction:row;flex-wrap:wrap;align-items:center;background:rgba(0,0,0,0);padding:0}.sbb-input-with-open-panel .sbb-chip-list{border-color:var(--sbb-form-input-color)}.sbb-chip-list .cdk-drag{cursor:grab}.sbb-chip-list.cdk-drop-list-dragging{cursor:grabbing}:is(.sbb-chip-input,.sbb-form-field .sbb-chip-input){max-width:100%;border:none;width:var(--sbb-chips-input-width);flex:1 0 var(--sbb-chips-input-width);min-height:calc(var(--sbb-chips-list-height) - 2*var(--sbb-border-width))}.sbb-chip-list-disabled :is(.sbb-chip-input,.sbb-form-field .sbb-chip-input){display:none}:where(html.sbb-lean) :is(.sbb-chip-input,.sbb-form-field .sbb-chip-input){padding-bottom:.4rem}.sbb-standard-chip{overflow:hidden;white-space:nowrap;text-overflow:ellipsis;font-size:var(--sbb-font-size);line-height:1;height:var(--sbb-chips-chip-height);border-radius:var(--sbb-chips-chip-height);color:var(--sbb-color-weak-accent);-webkit-text-fill-color:var(--sbb-color-weak-accent);background-color:var(--sbb-chips-background-color-active);max-width:100%;margin:var(--sbb-chips-chip-margin-vertical);padding:calc(.5*(var(--sbb-chips-chip-height) - var(--sbb-font-size) - 0.0666666667rem*var(--sbb-scaling-factor))) calc(0.9333333333rem*var(--sbb-scaling-factor))}.sbb-standard-chip:not(.sbb-chip-disabled):is(:hover,:focus){background-color:var(--sbb-chips-background-color-hover)}.sbb-standard-chip.sbb-chip-disabled{background-color:var(--sbb-chips-background-color-disabled);opacity:var(--sbb-chips-opacity)}.sbb-standard-chip.sbb-chip-disabled :is(.sbb-chip-remove,.sbb-chip-trailing-icon){cursor:default}.sbb-standard-chip.sbb-chip-with-trailing-icon{padding-right:calc(0.2666666667rem*var(--sbb-scaling-factor) + var(--sbb-icon-size-default) + var(--sbb-chips-chip-padding-horizontal));position:relative}.sbb-standard-chip :is(.sbb-chip-remove,.sbb-chip-trailing-icon){position:absolute;top:50%;transform:translateY(-50%);cursor:pointer;margin-left:var(--sbb-chips-chip-padding-horizontal);right:var(--sbb-chips-chip-padding-horizontal)}"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.ChangeDetectorRef }, { type: i1$2.NgForm, decorators: [{
                    type: Optional
                }] }, { type: i1$2.FormGroupDirective, decorators: [{
                    type: Optional
                }] }, { type: i2.SbbErrorStateMatcher }, { type: i1$2.NgControl, decorators: [{
                    type: Optional
                }, {
                    type: Self
                }] }], propDecorators: { role: [{
                type: Input
            }], userAriaDescribedBy: [{
                type: Input,
                args: ['aria-describedby']
            }], errorStateMatcher: [{
                type: Input
            }], value: [{
                type: Input
            }], required: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }], placeholder: [{
                type: Input
            }], disabled: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }], ariaOrientation: [{
                type: Input,
                args: ['aria-orientation']
            }], tabIndex: [{
                type: Input
            }], valueChange: [{
                type: Output
            }], chips: [{
                type: ContentChildren,
                args: [SBB_CHIP, {
                        // We need to use `descendants: true`, because Ivy will no longer match
                        // indirect descendants if it's left as false.
                        descendants: true,
                    }]
            }] } });

const CHIP_DECLARATIONS = [SbbChipList, SbbChip, SbbChipInput, SbbChipRemove, SbbChipTrailingIcon];
class SbbChipsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbChipsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.7", ngImport: i0, type: SbbChipsModule, imports: [SbbCommonModule, SbbIconModule, SbbChipList, SbbChip, SbbChipInput, SbbChipRemove, SbbChipTrailingIcon], exports: [SbbChipList, SbbChip, SbbChipInput, SbbChipRemove, SbbChipTrailingIcon] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbChipsModule, providers: [
            {
                provide: SBB_CHIPS_DEFAULT_OPTIONS,
                useValue: {
                    separatorKeyCodes: [ENTER],
                },
            },
        ], imports: [SbbCommonModule, SbbIconModule, SbbChip] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbChipsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [SbbCommonModule, SbbIconModule, ...CHIP_DECLARATIONS],
                    exports: CHIP_DECLARATIONS,
                    providers: [
                        {
                            provide: SBB_CHIPS_DEFAULT_OPTIONS,
                            useValue: {
                                separatorKeyCodes: [ENTER],
                            },
                        },
                    ],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { SBB_CHIP, SBB_CHIPS_DEFAULT_OPTIONS, SBB_CHIP_LIST, SBB_CHIP_REMOVE, SBB_CHIP_TRAILING_ICON, SbbChip, SbbChipInput, SbbChipList, SbbChipRemove, SbbChipTrailingIcon, SbbChipsModule };
